import {baseVarNames, optNames, bitsetString} from './bitsetData';

/**
 * Dekodiert einen Opt-Eintrag inklusive Bitflag-Logik und gibt EINEN <li>-Eintrag zurück,
 * in dem alle gesetzten Bits (zB. "P. Def" und "M. Def") kombiniert werden und der gemeinsame Wert angehängt wird.
 *
 * @param {object} resource - Das Ressourcenobjekt
 * @param {string} optTypeKey - z.B. "random_option_awaken_type_01"
 * @param {string} var1Key - z.B. "random_option_awaken_value1_01"
 * @param {string} var2Key - z.B. "random_option_awaken_value2_01"
 * @param {string} [color] - Optional: CSS-Farbwert, der dem <li> hinzugefügt wird
 * @returns {string} - Ein einzelner <li>-Eintrag oder "" bei ungültigen Daten
 */
function decodeOptFlagsSingleLine(resource, optTypeKey, var1Key, var2Key, color = '') {
  let optType = parseInt(resource[optTypeKey], 10) || 0;
  if (!optType) return '';
  const originalOpt = optType;
  if (optType === 98) optType = 96;
  if (optType === 99) optType = 97;
  const mapForType = optNames[optType];
  if (!mapForType) return '';
  const bitmask = Math.floor(parseFloat(resource[var1Key] || '0'));
  let value = parseFloat(resource[var2Key] || '0');
  if (originalOpt === 98 || originalOpt === 99) {
    value *= 100;
  }
  value = parseFloat(value.toFixed(2));
  let postfix = '';
  if (originalOpt === 98 || originalOpt === 99) {
    postfix = '%';
  }
  // Sammle alle gesetzten Bits und löse sie über optNames -> bitsetString auf:
  const labels = [];
  for (const [bitString, code] of Object.entries(mapForType)) {
    const bitInt = parseInt(bitString, 10);
    if ((bitmask & bitInt) === bitInt) {
      let text = bitsetString[code] || '';
      // Entferne Platzhalter, falls vorhanden (z.B. "#@val@#")
      text = text.replace(/#@[^#]+@#/g, '').trim();
      if (text) {
        labels.push(text);
      }
    }
  }
  if (labels.length === 0) return '';
  const combinedLabel = labels.join(' / ');
  const finalLine = `${combinedLabel} ${value}${postfix}`;
  // Falls ein Farbwert übergeben wurde, in style einfügen:
  return `<li${color ? ' style="color:' + color + ';"' : ''}>${finalLine}</li>`;
}

/**
 * buildBitsetText - Baut den vollständigen HTML-Tooltip (als <ul>) für ein Item auf,
 * bestehend aus:
 *  1) Base‑Werten
 *  2) Normalen Opt‑Werten
 *  3) Enhance
 *  4) Random Option Awaken (01–10) in hellblau
 *  5) Random Option Identified (01–10) in helllila
 */
export function buildBitsetText(resource) {
  let result = '';

  // 1) Base‑Werte verarbeiten (inklusive base_var2)
  for (let i = 0; i < 4; i++) {
    const baseTypeKey = `base_type_${i}`;
    const baseVar1Key = `base_var1_${i}`;
    const baseVar2Key = `base_var2_${i}`;
    const baseType = parseInt(resource[baseTypeKey], 10) || 0;
    if (!baseType) continue;
    const code = baseVarNames[baseType];
    if (!code) continue;
    let text = bitsetString[code] || `Code:${code}`;
    const val1 = parseFloat(resource[baseVar1Key] || '0');
    const val2 = parseFloat(resource[baseVar2Key] || '0');
    const finalValue = val1 + resource.level * val2;
    const roundedValue = Math.round(finalValue);
    const hasPlaceholder = /#@[^#]+@#/.test(text);
    if (hasPlaceholder) {
      // Ersetze den Platzhalter durch finalValue
      text = text.replace(/#@[^#]+@#/g, roundedValue);
    } else {
      // Falls kein Platzhalter: Wert anhängen
      // z.B. "P. Def" => "P. Def 123.45"
      text += ` ${roundedValue}`;
    }
    result += `<li>${text}</li>`;
  }

  // 2) Normale Opt‑Werte (4 Einträge)
  for (let i = 0; i < 4; i++) {
    const optTypeKey = `opt_type_${i}`;
    const var1Key = `opt_var1_${i}`;
    const var2Key = `opt_var2_${i}`;
    result += decodeOptFlagsSingleLine(resource, optTypeKey, var1Key, var2Key);
  }

  //// 3) Enhance-Wert
  //if (resource.enhance != null && resource.enhance !== "") {
  //  result += `<li>Enhance: ${resource.enhance}</li>`;
  //}

  // 4) Random Option Awaken (01 bis 10) – hellblau (#0485b5)
  for (let i = 1; i <= 10; i++) {
    const idx = i.toString().padStart(2, '0');
    const optTypeKey = `random_option_awaken_type_${idx}`;
    const var1Key = `random_option_awaken_value1_${idx}`;
    const var2Key = `random_option_awaken_value2_${idx}`;
    result += decodeOptFlagsSingleLine(resource, optTypeKey, var1Key, var2Key, '#0485b5');
  }

  // 5) Random Option Identified (01 bis 10) – helllila (#c801db)
  for (let i = 1; i <= 10; i++) {
    const idx = i.toString().padStart(2, '0');
    const optTypeKey = `random_option_identified_type_${idx}`;
    const var1Key = `random_option_identified_value1_${idx}`;
    const var2Key = `random_option_identified_value2_${idx}`;
    result += decodeOptFlagsSingleLine(resource, optTypeKey, var1Key, var2Key, '#c801db');
  }

  if (result) {
    result = `<ul style="margin:0; padding-left:20px;">${result}</ul>`;
  }
  return result;
}
